import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Product } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockedStorageDropdownActions } from '../components/interaction/BlockedStorageDropdownActions';
import { EligibleStorageDropdownActions } from '../components/interaction/EligibleStorageDropdownActions';
import { EligibleStorage, getFinancialStatus } from '../models/EligibleStorage';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const formatDimensions = (width: number, height: number, length: number): string => {
  return `${width} x ${height} x ${length}`;
};

const formatCurrency = (amount: number | null | undefined, currency: string): string => {
  if (amount === null || amount === undefined) return '-';
  return formatCurrencyAmount(amount, currency || 'EUR');
};

const convertToTanStackColumns = (agGridColumns: ColDef[], t: (key: string) => string): ColumnDef<any>[] =>
  agGridColumns.map((col) => {
    let headerLabel = col.headerName || 'Columna';

    if (headerLabel?.includes('table.')) {
      headerLabel = t(headerLabel);
    }

    return {
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: headerLabel,
    };
  });

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onStorageAction?: (action: 'removeFromPreinvoice' | 'block', storage: EligibleStorage) => void;
}

export const useAgGridColumns = ({ selectedColumns = [], onStorageAction }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('eligibleStorageModule');
  const currency = useCurrency();

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'product',
        headerName: t('table.product'),
        minWidth: 200,
        valueGetter: (params) => params.data?.product?.name ?? '-',
        cellRenderer: (params: any) => {
          const product = params.data?.product;
          if (!product) return <span className="text-sm">-</span>;

          return (
            <Product
              id={product.id}
              name={product.name}
              src=""
              code={product.code}
              enableLineClamp={true}
              showTooltip={true}
            />
          );
        },
      },
      {
        field: 'client',
        headerName: t('table.client'),
        minWidth: 180,
        valueGetter: (params) => params.data?.team?.name ?? '-',
        cellRenderer: (params: any) => {
          const team = params.data?.team;
          if (!team || !team.name) return <span className="text-sm">-</span>;

          return <span className="text-sm">{team.name}</span>;
        },
      },
      {
        field: 'storage_date',
        headerName: t('table.date'),
        minWidth: 120,
        valueGetter: (params) => params.data?.storage_date ?? null,
        cellRenderer: (params: any) => <span className="text-sm">{formatDate(params.data?.storage_date)}</span>,
      },
      {
        field: 'dimensions',
        headerName: t('table.dimensions'),
        minWidth: 140,
        valueGetter: (params) => {
          const data = params.data;
          if (!data) return '-';
          return formatDimensions(data.width_m, data.height_m, data.length_m);
        },
        cellRenderer: (params: any) => {
          const data = params.data;
          if (!data) return <span className="text-sm">-</span>;

          return <span className="text-sm">{formatDimensions(data.width_m, data.height_m, data.length_m)}</span>;
        },
      },
      {
        field: 'quantity',
        headerName: t('table.units'),
        minWidth: 100,
        valueGetter: (params) => params.data?.quantity ?? 0,
        cellRenderer: (params: any) => <span className="text-sm font-medium">{params.data?.quantity ?? 0}</span>,
      },
      {
        field: 'unit_volume_m3',
        headerName: t('table.unitVolume'),
        minWidth: 120,
        valueGetter: (params) => params.data?.unit_volume_m3 ?? 0,
        cellRenderer: (params: any) => (
          <span className="text-sm">{params.data?.unit_volume_m3?.toFixed(4) ?? '0'} m³</span>
        ),
      },
      {
        field: 'total_volume_m3',
        headerName: t('table.totalVolume'),
        minWidth: 120,
        valueGetter: (params) => params.data?.total_volume_m3 ?? 0,
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium">{params.data?.total_volume_m3?.toFixed(2) ?? '0'} m³</span>
        ),
      },
      {
        field: 'period',
        headerName: t('table.period'),
        minWidth: 100,
        valueGetter: (params) => params.data?.period ?? '-',
        cellRenderer: (params: any) => <span className="text-sm">{params.data?.period ?? '-'}</span>,
      },
      {
        field: 'applied_rate',
        headerName: t('table.appliedRate'),
        minWidth: 140,
        valueGetter: (params) => params.data?.applied_rate ?? null,
        cellRenderer: (params: any) => {
          const rate = params.data?.applied_rate;
          if (!rate) return <span className="text-sm">-</span>;
          return <span className="text-sm">{rate}</span>;
        },
      },
      {
        field: 'base_amount',
        headerName: t('table.baseAmount'),
        minWidth: 140,
        valueGetter: (params) => params.data?.base_amount ?? null,
        cellRenderer: (params: any) => {
          const amount = params.data?.base_amount;
          return <span className="text-sm font-medium">{formatCurrency(amount, currency)}</span>;
        },
      },
      {
        field: 'total_amount',
        headerName: t('table.totalAmount'),
        minWidth: 150,
        valueGetter: (params) => params.data?.total_amount ?? null,
        cellRenderer: (params: any) => {
          const amount = params.data?.total_amount;
          return <span className="text-sm font-semibold text-primary">{formatCurrency(amount, currency)}</span>;
        },
      },
      {
        field: 'financialStatus',
        headerName: t('table.financialStatus'),
        minWidth: 140,
        valueGetter: (params) => {
          const data = params.data;
          if (!data) return 'pending';
          return getFinancialStatus(data);
        },
        cellRenderer: (params: any) => {
          const data = params.data;
          if (!data) return null;

          const status = getFinancialStatus(data);
          const statusConfig: Record<string, { bg: string; text: string }> = {
            pending: {
              bg: 'bg-card-border light:bg-bg-warning',
              text: 'text-warning',
            },
            preinvoiced: {
              bg: 'bg-card-border light:bg-bg-info',
              text: 'text-info',
            },
            invoiced: {
              bg: 'bg-bg-success',
              text: 'text-success',
            },
          };

          const config = statusConfig[status] || {
            bg: 'bg-card-border',
            text: 'text-text',
          };

          return <Badge className={`text-xs ${config.bg} ${config.text}`}>{t(`financialStatus.${status}`)}</Badge>;
        },
      },
      {
        field: 'preinvoice_id',
        headerName: t('table.preinvoiceNumber'),
        minWidth: 140,
        valueGetter: (params) => params.data?.preinvoice_id ?? null,
        cellRenderer: (params: any) => {
          const preinvoiceId = params.data?.preinvoice_id;
          if (!preinvoiceId) return <span className="text-sm">-</span>;

          return <span className="text-sm text-primary cursor-pointer hover:underline">#{preinvoiceId}</span>;
        },
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoiceNumber'),
        minWidth: 140,
        valueGetter: (params) => params.data?.invoice_id ?? null,
        cellRenderer: (params: any) => {
          const invoiceId = params.data?.invoice_id;
          if (!invoiceId) return <span className="text-sm">-</span>;

          return <span className="text-sm text-primary cursor-pointer hover:underline">#{invoiceId}</span>;
        },
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          if (!onStorageAction) return null;

          return (
            <div className="flex justify-center w-full">
              <EligibleStorageDropdownActions storage={params.data} onAction={onStorageAction} />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [t, currency, onStorageAction]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';
      return selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns]);

  return {
    filteredColumnDefs,
    tanStackColumns,
  };
};

interface UseBlockedStoragesColumnsProps {
  selectedColumns?: string[];
  onUnblockStorage?: (blockId: number) => void;
}

/**
 * useBlockedStoragesColumns - Hook para definir las columnas de la tabla AG Grid de storages bloqueados
 *
 * Define todas las columnas disponibles para la tabla de storages bloqueados,
 * incluyendo las mismas columnas que elegibles más columnas específicas de bloqueo.
 */
export const useBlockedStoragesColumns = ({
  selectedColumns = [],
  onUnblockStorage,
}: UseBlockedStoragesColumnsProps) => {
  const { t } = useTranslation('eligibleStorageModule');
  const currency = useCurrency();

  const allColumnDefs: ColDef[] = useMemo(() => {
    // Obtener el storage desde blockedStorage (puede venir en storage o directamente)
    const getStorage = (params: any) => params.data?.storage ?? params.data;

    const baseColumns: CustomColDef[] = [
      {
        field: 'product',
        headerName: t('table.product'),
        minWidth: 200,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.product?.name ?? '-';
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const product = storage?.product;
          if (!product) return <span className="text-sm">-</span>;

          return (
            <Product
              id={product.id}
              name={product.name}
              src=""
              code={product.code}
              enableLineClamp={true}
              showTooltip={true}
            />
          );
        },
      },
      {
        field: 'client',
        headerName: t('table.client'),
        minWidth: 180,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.team?.name ?? '-';
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const team = storage?.team;
          if (!team || !team.name) return <span className="text-sm">-</span>;

          return <span className="text-sm">{team.name}</span>;
        },
      },
      {
        field: 'storage_date',
        headerName: t('table.date'),
        minWidth: 120,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.storage_date ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          return <span className="text-sm">{formatDate(storage?.storage_date)}</span>;
        },
      },
      {
        field: 'dimensions',
        headerName: t('table.dimensions'),
        minWidth: 140,
        valueGetter: (params) => {
          const storage = getStorage(params);
          if (!storage) return '-';
          return formatDimensions(storage.width_m, storage.height_m, storage.length_m);
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          if (!storage) return <span className="text-sm">-</span>;

          return (
            <span className="text-sm">{formatDimensions(storage.width_m, storage.height_m, storage.length_m)}</span>
          );
        },
      },
      {
        field: 'quantity',
        headerName: t('table.units'),
        minWidth: 100,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.quantity ?? 0;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          return <span className="text-sm font-medium">{storage?.quantity ?? 0}</span>;
        },
      },
      {
        field: 'unit_volume_m3',
        headerName: t('table.unitVolume'),
        minWidth: 120,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.unit_volume_m3 ?? 0;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          return <span className="text-sm">{storage?.unit_volume_m3?.toFixed(4) ?? '0'} m³</span>;
        },
      },
      {
        field: 'total_volume_m3',
        headerName: t('table.totalVolume'),
        minWidth: 120,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.total_volume_m3 ?? 0;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          return <span className="text-sm font-medium">{storage?.total_volume_m3?.toFixed(2) ?? '0'} m³</span>;
        },
      },
      {
        field: 'period',
        headerName: t('table.period'),
        minWidth: 100,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.period ?? '-';
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          return <span className="text-sm">{storage?.period ?? '-'}</span>;
        },
      },
      {
        field: 'applied_rate',
        headerName: t('table.appliedRate'),
        minWidth: 140,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.applied_rate ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const rate = storage?.applied_rate;
          if (!rate) return <span className="text-sm">-</span>;
          return <span className="text-sm">{rate}</span>;
        },
      },
      {
        field: 'base_amount',
        headerName: t('table.baseAmount'),
        minWidth: 140,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.base_amount ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const amount = storage?.base_amount;
          return <span className="text-sm font-medium">{formatCurrency(amount, currency)}</span>;
        },
      },
      {
        field: 'total_amount',
        headerName: t('table.totalAmount'),
        minWidth: 150,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.total_amount ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const amount = storage?.total_amount;
          return <span className="text-sm font-semibold text-primary">{formatCurrency(amount, currency)}</span>;
        },
      },
      {
        field: 'financialStatus',
        headerName: t('table.financialStatus'),
        minWidth: 140,
        valueGetter: () => {
          return 'blocked'; // Siempre bloqueado en esta vista
        },
        cellRenderer: () => {
          return (
            <Badge className="text-xs bg-card-border light:bg-bg-warning text-warning">
              {t('financialStatus.blocked', 'Bloqueado')}
            </Badge>
          );
        },
      },
      {
        field: 'preinvoice_id',
        headerName: t('table.preinvoiceNumber'),
        minWidth: 140,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.preinvoice_id ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const preinvoiceId = storage?.preinvoice_id;
          if (!preinvoiceId) return <span className="text-sm">-</span>;

          return <span className="text-sm text-primary cursor-pointer hover:underline">#{preinvoiceId}</span>;
        },
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoiceNumber'),
        minWidth: 140,
        valueGetter: (params) => {
          const storage = getStorage(params);
          return storage?.invoice_id ?? null;
        },
        cellRenderer: (params: any) => {
          const storage = getStorage(params);
          const invoiceId = storage?.invoice_id;
          if (!invoiceId) return <span className="text-sm">-</span>;

          return <span className="text-sm text-primary cursor-pointer hover:underline">#{invoiceId}</span>;
        },
      },
      // Columnas específicas de bloqueo
      {
        field: 'block_reason',
        headerName: t('table.block_reason', 'Motivo de bloqueo'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => {
          // La API puede devolver 'reason' o 'storage_block_reason'
          const reason = params.data?.reason ?? params.data?.storage_block_reason;
          return reason?.name ?? '-';
        },
        cellRenderer: (params: any) => {
          const reason = params.data?.reason ?? params.data?.storage_block_reason;
          if (!reason?.name) return <span className="text-sm">-</span>;

          return <span className="text-sm">{reason.name}</span>;
        },
      },
      {
        field: 'block_comment',
        headerName: t('table.block_comment', 'Comentario'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => params.data?.comment ?? '-',
        cellRenderer: (params: any) => {
          const comment = params.data?.comment;
          if (!comment) return <span className="text-sm">-</span>;

          return (
            <span className="text-sm" title={comment}>
              {comment}
            </span>
          );
        },
      },
      {
        field: 'blocked_by',
        headerName: t('table.blocked_by', 'Bloqueado por'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => {
          const user = params.data?.user;
          return user?.name ?? '-';
        },
        cellRenderer: (params: any) => {
          const user = params.data?.user;
          if (!user?.name) return <span className="text-sm">-</span>;

          return <span className="text-sm">{user.name}</span>;
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at', 'Fecha de bloqueo'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => params.data?.created_at ?? null,
        cellRenderer: (params: any) => <span className="text-sm">{formatDate(params.data?.created_at)}</span>,
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          if (!onUnblockStorage) return null;

          return (
            <div className="flex justify-center w-full">
              <BlockedStorageDropdownActions
                blockId={params.data?.id}
                onAction={(action, blockId) => {
                  if (action === 'unblock') {
                    onUnblockStorage(blockId);
                  }
                }}
              />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [t, currency, onUnblockStorage]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    // La columna de acciones siempre debe estar visible
    const actionsColumn = allColumnDefs.find((col) => col.field === 'actions');
    const otherColumns = allColumnDefs.filter((col) => {
      const field = col.field || '';
      return field !== 'actions' && selectedColumns.includes(field);
    });

    // Si hay columna de acciones, añadirla al final
    return actionsColumn ? [...otherColumns, actionsColumn] : otherColumns;
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  return {
    filteredColumnDefs,
    tanStackColumns,
  };
};
